import { computed, createApp, h, ref, watch } from 'vue'
import { debounce } from 'lodash'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

import { v4 as uuidv4 } from 'uuid'
import { events$ } from '@shared/services'
import config from '@shared/config'

import InfoWindowPopup from '@shared/components/CareProvider/InfoWindow'

import swissPaths from '@shared/components/CareProvider/switzerland.json'
import { GOOGLE_MAP_OPTIONS, SWISS_BOUNDING_BOX, ZOOM_DEFAULT } from '@shared/components/CareProvider/constants'
import { EVENT_CARE_PROVIDER_MODAL } from '@shared/config/events'

// GLOBAL DATA
let infoWindow
const libs = {
  InfoWindow: null,
  LatLng: () => {},
  LatLngBounds: null,
  Map: null,
  Marker: null,
  Poly: null,
  Polygon: null,
  Size: null,
}
let map
let swissBounds
let swissPolygon

export default function useGoogleMap() {

  // DATA
  const hoveredMarker = ref(null)
  const infoWindowContent = ref(null)
  const infoWindowMarker = ref(null)
  const infoWindowProps = ref({
    buttonLabel: '',
    careProvider: {},
    isActive: false
  })
  const markers = ref([])
  const selectedMarker = ref(null)

  // COMPUTED
  const isLibraryLoaded = computed(() => {
    return Object.values(libs).every(lib => lib !== null)
  })

  // METHODS
  const debouncedPanToSwissBounds = debounce(panToSwissBounds, 300)

  function drawMarkers(markerData) {
    removeAllMarkers()
    markerData.forEach(entry => {
      const customMarkerIcon = document.createElement('div')
      customMarkerIcon.innerHTML = `
        <svg class="custom-marker" data-zsr-number="${entry.zsrNumber}" viewBox="0 0 640 640">
          <path class="inner" d="M160 252.6C160 166.6 231.1 96 320 96C408.9 96 480 166.6 480 252.6C480 302.9 454.1 362 418.6 418.2C384.6 472 344.6 518.7 320 545.6C295.4 518.7 255.4 471.9 221.4 418.2C185.9 362 160 302.9 160 252.6zM240 256C240 300.2 275.8 336 320 336C364.2 336 400 300.2 400 256C400 211.8 364.2 176 320 176C275.8 176 240 211.8 240 256z"/>
          <path class="border" d="M160 252.6C160 166.6 231.1 96 320 96C408.9 96 480 166.6 480 252.6C480 302.9 454.1 362 418.6 418.2C384.6 472 344.6 518.7 320 545.6C295.4 518.7 255.4 471.9 221.4 418.2C185.9 362 160 302.9 160 252.6zM320 64C214 64 128 148.4 128 252.6C128 371.9 248.2 514.9 298.4 569.4C310.2 582.2 329.8 582.2 341.6 569.4C391.8 514.9 512 371.9 512 252.6C512 148.4 426 64 320 64zM368 256C368 282.5 346.5 304 320 304C293.5 304 272 282.5 272 256C272 229.5 293.5 208 320 208C346.5 208 368 229.5 368 256zM320 176C275.8 176 240 211.8 240 256C240 300.2 275.8 336 320 336C364.2 336 400 300.2 400 256C400 211.8 364.2 176 320 176z"/>
        </svg>
      `
      const marker = new libs.Marker({
        content: customMarkerIcon,
        map,
        position: entry.point,
        title: entry.zsrNumber,
      })

      markers.value.push(marker)
    })
  }

  function getMarkerEl(zsrNumber) {
    return document.querySelector(`.custom-marker[data-zsr-number="${zsrNumber}"`)
  }

  async function initGoogleMap({location, mapRef, zoomControl }) {
    if (!isLibraryLoaded.value) {
      await initLibrary()
    }

    const center = mapLocationToLatLng(location)
    swissBounds = new libs.LatLngBounds(SWISS_BOUNDING_BOX.min, SWISS_BOUNDING_BOX.max)
    map = initMap(center, mapRef, zoomControl)

    infoWindow = initInfoWindow()

    swissPolygon = initPolygon(map)

    return { infoWindow, map }
  }

  function initInfoWindow() {
    return new libs.InfoWindow({
      disableAutoPan: true,
      minWidth: 320,
      maxWidth: 320,
      pixelOffset: new libs.Size(0, -4),
    })
  }

  async function initLibrary() {
    setOptions({
      key: config.googleApiKey
    })

    // load needed library
    const [{ InfoWindow, Map, Polygon }, { AdvancedMarkerElement }, { poly }, { LatLng, LatLngBounds, Size }] = await Promise.all([
      importLibrary('maps'),
      importLibrary('marker'),
      importLibrary('geometry'),
      importLibrary('core'),
    ])

    libs.InfoWindow = InfoWindow
    libs.LatLng = LatLng
    libs.LatLngBounds = LatLngBounds
    libs.Map = Map
    libs.Marker = AdvancedMarkerElement
    libs.Poly = poly
    libs.Polygon = Polygon
    libs.Size = Size
  }

  function initMap(center, mapRef, zoomControl) {
    const options = {
      ...GOOGLE_MAP_OPTIONS,
      center,
      mapId: uuidv4(),
      zoom: ZOOM_DEFAULT,
      zoomControl,
    }

    return new libs.Map(mapRef, options)
  }

  function initPolygon() {
    const polygon = new libs.Polygon({
      paths: swissPaths,
      strokeColor: '#5BAC26',
      strokeOpacity: 1,
      strokeWeight: 1,
      fillOpacity: 0.2,
    })

    // this would be the right way, but we don't have a mapId
    // https://developers.google.com/maps/documentation/javascript/dds-boundaries/style-polygon#javascript
    polygon.setMap(map)

    return polygon

  }

  function isOutsideOfSwitzerland() {
    const point = { lat: map.getCenter().lat(), lng: map.getCenter().lng() }
    // the switzerland polygon contains the whole map without switzerland
    return libs.Poly.containsLocation(point, swissPolygon)
  }

  function mapBoundsToCoordinates(bounds) {
    const northEast = bounds.getNorthEast()
    const southWest = bounds.getSouthWest()
    return {
      minLatitude: southWest.lat(),
      maxLatitude: northEast.lat(),
      minLongitude: southWest.lng(),
      maxLongitude: northEast.lng(),
    }
  }

  function mapLocationToLatLng(location) {
    return new libs.LatLng({
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude)
    })
  }

  function panToSwissBounds() {
    if (!isOutsideOfSwitzerland(map)) return

    map.panToBounds(swissBounds)
  }

  function setMapCenter(location) {
    const center = mapLocationToLatLng(location)
    map.setCenter(center)
  }

  // Initialised once, for the first time user hovers/clicks marker
  function createInfoWindow({ buttonLabel, careProvider, isActive }) {
    infoWindowProps.value.buttonLabel = buttonLabel || ''
    infoWindowProps.value.careProvider = careProvider
    infoWindowProps.value.isActive = isActive

    if (!infoWindowContent.value) {
      const app = createApp({
        render: () => h(InfoWindowPopup, infoWindowProps.value)
      })

      const content = document.createElement('div')
      app.mount(content)

      const addButton = content.querySelector('#info-window-add-button')
      addButton?.addEventListener('click', () => {
        events$.emit(EVENT_CARE_PROVIDER_MODAL.ADD, { zsrNumber: selectedMarker.value.title })
      })

      infoWindowContent.value = content
  }
  }

  function closeCurrentInfoWindow() {
    infoWindow.close()
    infoWindowMarker.value = null
  }

  function openNewInfoWindow({ buttonLabel, careProvider, isActive }) {
    if (infoWindowMarker.value) {
      closeCurrentInfoWindow()
    }
    createInfoWindow({ buttonLabel, careProvider, isActive })
    infoWindowMarker.value = markers.value.find(m => m.title === careProvider.zsrNumber)
    infoWindow.setContent(infoWindowContent.value)
    infoWindow.open(map, infoWindowMarker.value)
  }

  function getMarkerByZsrNumber(zsrNumber) {
    return markers.value.find(m => m.title === zsrNumber)
  }

  function updateInfoWindow({ buttonLabel, careProvider, isActive }) {
    const mouseover = careProvider && !isActive
    const mouseout = !careProvider && !isActive
    const select = careProvider && isActive
    const deselect = !careProvider && isActive

    const isSameMarker = (mouseover || select) && infoWindowMarker.value ? infoWindowMarker.value.title === careProvider.zsrNumber : false
    const isMarkerAlreadySelected = infoWindowProps.value.isActive

    if (mouseover) {
      if (isSameMarker) return
      hoveredMarker.value = getMarkerByZsrNumber(careProvider.zsrNumber)
      return openNewInfoWindow({ buttonLabel, careProvider, isActive })
    }

    if (mouseout && !isMarkerAlreadySelected) {
      hoveredMarker.value = null
      return closeCurrentInfoWindow()
    }

    if (select) {
      selectedMarker.value = getMarkerByZsrNumber(careProvider.zsrNumber)
      if (!isSameMarker) {
        return openNewInfoWindow({ buttonLabel, careProvider, isActive })
      }
      infoWindowProps.value.isActive = !isMarkerAlreadySelected
      return
    }

    if (deselect) {
      selectedMarker.value = null
      infoWindowProps.value.isActive = false
      return
    }

  }

  function removeAllMarkers() {
    markers.value.forEach(marker => marker.setMap(null))
    markers.value = []
  }

  watch(
    () => hoveredMarker.value,
    (newVal, oldVal) => {
      if (oldVal) {
        const oldEl = getMarkerEl(oldVal.title)
        oldEl?.classList.remove('custom-marker--hovered')
      }
      if (newVal) {
        const newEl = getMarkerEl(newVal.title)
        newEl?.classList.add('custom-marker--hovered')
      }
    }
  )

  watch(
    () => selectedMarker.value,
    (newVal, oldVal) => {
      if (oldVal) {
        const oldEl = getMarkerEl(oldVal.title)
        oldEl?.classList.remove('custom-marker--selected')
      }
      if (newVal) {
        const newEl = getMarkerEl(newVal.title)
        newEl?.classList.add('custom-marker--selected')
      }
    }
  )


  return {
    debouncedPanToSwissBounds,
    drawMarkers,
    initGoogleMap,
    isOutsideOfSwitzerland,
    mapBoundsToCoordinates,
    setMapCenter,
    updateInfoWindow,
  }
}
